import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/ui/SearchableSelect';
import {
  Printer,
  Calendar,
  Tag,
  Plus,
  Trash2,
  Package,
  Sparkles,
  Building2,
  Barcode,
  Copy,
  Layers,
  CheckSquare,
  Square,
  ZoomIn,
  ZoomOut,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import StokSampleLabelSticker from './StokSampleLabelSticker';
import { extractNameAndCode } from '@/lib/extractItemCode';

const getIndonesianDate = (d = new Date()) => {
  const months = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
  ];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const KATEGORI_OPTIONS = [
  { id: 'bahan', label: 'Bahan Cair (Bibit/Pelarut)' },
  { id: 'packaging', label: 'Packaging (Botol/Tutup/Spray)' },
  { id: 'item-tambahan', label: 'Item Tambahan (Stiker/Box/Sablon)' },
];

const SUB_KATEGORI_MAP = {
  bahan: ['Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'],
  packaging: ['botol', 'tutup', 'spray', 'fullset'],
  'item-tambahan': ['Stiker', 'Box', 'Sablon', 'Hot Print', 'Gift Card', 'Coloring', 'Lainnya'],
};

export default function CetakSampleStokModal({
  isOpen,
  onClose,
  allStockData = {},
  initialItem = null,
}) {
  const [tanggal, setTanggal] = useState(getIndonesianDate());
  const [labelTag, setLabelTag] = useState('Sample');
  const [previewZoom, setPreviewZoom] = useState(1.4);
  const [isFormExpanded, setIsFormExpanded] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Form input tambah item baru
  const [katUtama, setKatUtama] = useState('bahan');
  const [subKat, setSubKat] = useState('Bibit');
  const [selectedStockId, setSelectedStockId] = useState('');
  const [formVendor, setFormVendor] = useState('');
  const [formNama, setFormNama] = useState('');
  const [formKode, setFormKode] = useState('');
  const [formQty, setFormQty] = useState(1);

  // Antrean item yang akan dicetak
  const [queueItems, setQueueItems] = useState([]);

  // Flatten available stock items based on category
  const availableStockItems = useMemo(() => {
    const {
      bahanList = [],
      botolList = [],
      tutupList = [],
      sprayList = [],
      fullsetList = [],
      itemTambahanList = [],
    } = allStockData;

    if (katUtama === 'bahan') {
      return bahanList
        .filter((b) => (subKat === 'semua' || !subKat ? true : b.kategori === subKat))
        .map((b) => ({
          id: `bahan-${b.id}`,
          rawId: b.id,
          nama: b.nama || '',
          vendor: b.vendor || (b.dari_client ? `Client (${b.client_owner || 'Client'})` : ''),
          kode: b.kode || b.batch_number || b.lot_number || '',
          kategori: b.kategori || 'Bibit',
          type: 'bahan',
        }));
    }

    if (katUtama === 'packaging') {
      let list = [];
      if (subKat === 'botol' || subKat === 'semua') {
        list.push(...botolList.map((i) => ({ ...i, _tipe: 'botol' })));
      }
      if (subKat === 'tutup' || subKat === 'semua') {
        list.push(...tutupList.map((i) => ({ ...i, _tipe: 'tutup' })));
      }
      if (subKat === 'spray' || subKat === 'semua') {
        list.push(...sprayList.map((i) => ({ ...i, _tipe: 'spray' })));
      }
      if (subKat === 'fullset' || subKat === 'semua') {
        list.push(...fullsetList.map((i) => ({ ...i, _tipe: 'fullset' })));
      }
      return list.map((p) => ({
        id: `pack-${p.id}`,
        rawId: p.id,
        nama: p.nama || '',
        vendor: p.vendor || (p.dari_client ? `Client (${p.client_owner || 'Client'})` : ''),
        kode: p.kode || p.sku || '',
        kategori: p._tipe || 'botol',
        type: 'packaging',
      }));
    }

    if (katUtama === 'item-tambahan') {
      return itemTambahanList
        .filter((i) => (subKat === 'semua' || !subKat ? true : i.kategori === subKat))
        .map((it) => ({
          id: `it-${it.id}`,
          rawId: it.id,
          nama: it.nama || '',
          vendor: it.vendor || it.brand || (it.dari_client ? `Client (${it.client_owner || 'Client'})` : ''),
          kode: it.kode || it.sku || '',
          kategori: it.kategori || 'Stiker',
          type: 'item-tambahan',
        }));
    }

    return [];
  }, [katUtama, subKat, allStockData]);

  // Sync subcategory options when main category changes
  useEffect(() => {
    const defaultSub = SUB_KATEGORI_MAP[katUtama]?.[0] || '';
    setSubKat(defaultSub);
    setSelectedStockId('');
  }, [katUtama]);

  // Dropdown options memoized for SearchableSelect
  const kategoriOptions = useMemo(() => {
    return KATEGORI_OPTIONS.map((k) => ({
      value: k.id,
      label: k.label,
    }));
  }, []);

  const subKategoriOptions = useMemo(() => {
    return (SUB_KATEGORI_MAP[katUtama] || []).map((s) => ({
      value: s,
      label: s.charAt(0).toUpperCase() + s.slice(1),
    }));
  }, [katUtama]);

  const stockDropdownOptions = useMemo(() => {
    const list = [
      {
        value: 'custom',
        label: '✏️ -- Ketik Manual Sendiri --',
        sublabel: 'Kosongkan pilihan stok untuk mengetik bebas',
      },
    ];
    availableStockItems.forEach((s) => {
      const parts = [];
      if (s.vendor) parts.push(`Vendor: ${s.vendor}`);
      if (s.kode) parts.push(`Kode: ${s.kode}`);
      list.push({
        value: s.id,
        label: s.nama,
        sublabel: parts.join(' • '),
      });
    });
    return list;
  }, [availableStockItems]);

  // Handle auto-fill when user selects an item from stock dropdown
  const handleSelectStockItem = (val) => {
    setSelectedStockId(val);
    if (!val || val === 'custom') {
      return;
    }
    const found = availableStockItems.find((s) => s.id === val);
    if (found) {
      const extracted = extractNameAndCode(found.nama, found.kode || '');
      setFormNama(extracted.name);
      setFormVendor(found.vendor || '');
      setFormKode(extracted.code);
    }
  };

  // Prepopulate if initialItem is provided (e.g. user clicked print from a specific row)
  useEffect(() => {
    if (isOpen) {
      setTanggal(getIndonesianDate());
      if (initialItem && queueItems.length === 0) {
        const rawName = initialItem.nama || initialItem.item_name || '';
        const rawCode = initialItem.kode || initialItem.batch_number || '';
        const extracted = extractNameAndCode(rawName, rawCode);
        setQueueItems([
          {
            id: `item-${Date.now()}`,
            vendor: initialItem.vendor || initialItem.brand || '',
            nama_item: extracted.name,
            kode: extracted.code,
            qty: 1,
            selected: true,
          },
        ]);
      }
    }
  }, [isOpen, initialItem]);

  // Add item to queue
  const handleAddToQueue = () => {
    if (!formNama.trim()) {
      toast.error('Nama item / bibit wajib diisi!');
      return;
    }

    const extracted = extractNameAndCode(formNama, formKode);

    const newItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      vendor: formVendor.trim(),
      nama_item: extracted.name,
      kode: extracted.code,
      qty: Math.max(1, Number(formQty) || 1),
      selected: true,
      kategori: katUtama,
      subKategori: subKat,
    };

    setQueueItems((prev) => [...prev, newItem]);
    // Reset form inputs for next addition
    setFormNama('');
    setFormKode('');
    setFormQty(1);
    setSelectedStockId('');
    toast.success(`Berhasil menambahkan "${newItem.nama_item}" ke daftar cetak!`);
  };

  // Update item in queue
  const handleUpdateQueueItem = (id, field, value) => {
    setQueueItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  // Delete item from queue
  const handleRemoveQueueItem = (id) => {
    setQueueItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Duplicate item in queue
  const handleDuplicateQueueItem = (id) => {
    const itemToDup = queueItems.find((i) => i.id === id);
    if (!itemToDup) return;

    const dupItem = {
      ...itemToDup,
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      selected: true,
    };

    setQueueItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx === -1) return [...prev, dupItem];
      const next = [...prev];
      next.splice(idx + 1, 0, dupItem);
      return next;
    });

    toast.success(`"${itemToDup.nama_item}" berhasil diduplikat!`);
  };

  // Toggle select
  const handleToggleSelect = (id) => {
    setQueueItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, selected: !item.selected } : item))
    );
  };

  // Select all / Deselect all
  const isAllSelected = queueItems.length > 0 && queueItems.every((i) => i.selected);
  const handleToggleSelectAll = () => {
    const nextVal = !isAllSelected;
    setQueueItems((prev) => prev.map((item) => ({ ...item, selected: nextVal })));
  };

  // =========================================================================
  // PENOMORAN OTOMATIS:
  // "cuman nanti kalau misalkan jumlahnya item yang sama lebih dari 1,
  // nanti setelah nama item disampingnya ada nomor (1) atau (2) dan berikutnya"
  // =========================================================================
  const stickersToPrint = useMemo(() => {
    const list = [];
    queueItems.forEach((item) => {
      if (!item.selected) return;

      const qty = Math.max(1, Number(item.qty) || 1);
      const extracted = extractNameAndCode(item.nama_item, item.kode);

      if (qty === 1) {
        // Hanya 1 item -> Tanpa nomor disampingnya
        list.push({
          id: `${item.id}-0`,
          vendor: item.vendor,
          itemName: extracted.name,
          code: extracted.code,
          tanggal,
          labelTag,
        });
      } else {
        // Lebih dari 1 item -> Disamping nama item diberi nomor (1), (2), (3)...
        for (let copyIdx = 1; copyIdx <= qty; copyIdx++) {
          list.push({
            id: `${item.id}-${copyIdx}`,
            vendor: item.vendor,
            itemName: `${extracted.name} (${copyIdx})`,
            code: extracted.code,
            tanggal,
            labelTag,
          });
        }
      }
    });
    return list;
  }, [queueItems, tanggal, labelTag]);

  // Native Print Dialog Handler
  const handlePrint = () => {
    if (stickersToPrint.length === 0) {
      toast.error('Pilih minimal 1 stiker untuk dicetak.');
      return;
    }

    const origTitle = document.title;
    document.title = `Stiker-Sample-QC-${getIndonesianDate().replace(/\s+/g, '-')}`;

    try {
      window.print();
    } finally {
      setTimeout(() => {
        document.title = origTitle;
      }, 500);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Global Isolated Print Styles */}
      <style>{`
        @media screen {
          #stok-sample-print-container {
            display: none !important;
          }
        }
        @media print {
          body > #root,
          [data-radix-portal],
          [role="dialog"] {
            display: none !important;
          }
          #stok-sample-print-container {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }
          @page {
            size: A4 portrait !important;
            margin: 0 !important;
          }
          html, body {
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Main Interactive Dialog Modal */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl w-[96vw] max-h-[94vh] flex flex-col p-0 gap-0 overflow-hidden bg-white rounded-2xl border border-[#E6D5B8] shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between pl-5 pr-14 sm:pr-16 py-4 border-b border-[#E6D5B8]/80 bg-[#FAF7F2] shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#302621] text-[#D4AF37] flex items-center justify-center shadow-xs border border-[#8C6208]/30">
                <Printer className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-[#302621] flex items-center gap-2">
                  <span>Cetak Label Sample (QC / Kedatangan Stok)</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#8C6208]/15 text-[#8C6208] border border-[#8C6208]/30 font-bold">
                    40 × 20 mm
                  </span>
                </DialogTitle>
                <p className="text-xs text-[#7A695B] mt-0.5">
                  Atur nama vendor, nama item, kode, dan cetak stiker sample dengan penomoran otomatis
                </p>
              </div>
            </div>

            {/* Quick date setting */}
            <div className="hidden sm:flex items-center gap-2">
              <Label className="text-xs text-slate-500 flex items-center gap-1 font-medium">
                <Calendar className="w-3.5 h-3.5 text-[#8C6208]" /> Tanggal:
              </Label>
              <Input
                type="text"
                value={tanggal}
                onChange={(e) => setTanggal(e.target.value)}
                placeholder="04 Oktober 2026"
                className="h-8 w-44 text-xs font-semibold bg-white border-[#E6D5B8]"
              />
            </div>
          </div>

          {/* Modal Body: Full Width for spacious editing */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 flex flex-col gap-4 min-h-0 bg-[#FCFBF9]">
            {/* Form Box: Tambah Item Baru (Collapsible / Accordion) */}
            <div className="bg-white rounded-xl border border-[#E6D5B8] shadow-2xs overflow-hidden transition-all shrink-0">
                {/* Header Collapsible Trigger */}
                <button
                  type="button"
                  onClick={() => setIsFormExpanded(!isFormExpanded)}
                  className={`w-full flex items-center justify-between p-3.5 transition-colors cursor-pointer text-left ${
                    isFormExpanded ? 'bg-[#FAF7F2] border-b border-[#F0E8D9]' : 'bg-white hover:bg-[#FAF7F2]'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 rounded-lg bg-[#8C6208]/10 text-[#8C6208] flex items-center justify-center shrink-0 border border-[#8C6208]/20">
                      <Plus className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-[#302621] uppercase tracking-wider flex items-center gap-2">
                        <span>Tambah Item ke Antrean</span>
                        <span className="text-[10px] lowercase font-normal text-slate-400">
                          ({isFormExpanded ? 'bisa disembunyikan' : 'klik untuk buka'})
                        </span>
                      </h4>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {isFormExpanded
                          ? 'Pilih kategori, cari dari stok atau ketik bebas'
                          : 'Form input sedang disembunyikan agar tampilan lebih lega'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-bold text-[#8C6208] bg-[#8C6208]/10 px-2 py-0.5 rounded-full">
                      {isFormExpanded ? 'Tutup Panel' : 'Buka Panel'}
                    </span>
                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                      {isFormExpanded ? (
                        <ChevronUp className="w-4 h-4 text-[#8C6208]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#8C6208]" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Form Inputs (Hanya tampil saat isFormExpanded = true) */}
                {isFormExpanded && (
                  <div className="p-4 space-y-3.5 animate-in fade-in duration-150">
                    {/* Baris 1: Pilihan Kategori Utama & Sub Kategori */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[11px] font-bold text-slate-700 mb-1 block">
                          1. Kategori Utama
                        </Label>
                        <SearchableSelect
                          value={katUtama}
                          onValueChange={setKatUtama}
                          options={kategoriOptions}
                          placeholder="Pilih Kategori..."
                          searchPlaceholder="Cari kategori..."
                          className="h-8 text-xs bg-[#FAF7F2] border-slate-300 rounded-md"
                        />
                      </div>

                      <div>
                        <Label className="text-[11px] font-bold text-slate-700 mb-1 block">
                          2. Sub Kategori
                        </Label>
                        <SearchableSelect
                          value={subKat}
                          onValueChange={setSubKat}
                          options={subKategoriOptions}
                          placeholder="Pilih Sub Kategori..."
                          searchPlaceholder="Cari sub kategori..."
                          className="h-8 text-xs bg-[#FAF7F2] border-slate-300 rounded-md"
                        />
                      </div>
                    </div>

                    {/* Baris 2: Pilih dari Master Stok (Searchable / Otomatis Isi Data) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-[11px] font-bold text-slate-700 flex items-center gap-1">
                          <Package className="w-3 h-3 text-[#8C6208]" /> 3. Pilih dari Stok (Otomatis Isi Data)
                        </Label>
                        <span className="text-[10px] text-[#8C6208] font-medium">
                          {availableStockItems.length} item tersedia
                        </span>
                      </div>
                      <SearchableSelect
                        value={selectedStockId}
                        onValueChange={handleSelectStockItem}
                        options={stockDropdownOptions}
                        placeholder="-- Pilih item terdaftar atau ketik manual di bawah --"
                        searchPlaceholder="Ketik untuk mencari dari 414 item stok (nama, vendor, kode)..."
                        className="h-8 text-xs bg-[#FAF7F2] border-slate-300 rounded-md"
                        popoverClassName="w-[var(--radix-popover-trigger-width)] min-w-[360px]"
                      />
                    </div>

                    {/* Baris 3: Edit Detail (Vendor, Nama Item, Kode, Qty) */}
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 pt-1">
                      <div className="sm:col-span-4">
                        <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                          Nama Vendor *
                        </Label>
                        <Input
                          type="text"
                          value={formVendor}
                          onChange={(e) => setFormVendor(e.target.value)}
                          placeholder="Contoh: Luzi / Bell / CPL"
                          className="h-8 text-xs font-medium bg-white"
                        />
                      </div>

                      <div className="sm:col-span-4">
                        <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                          Nama Bibit / Item *
                        </Label>
                        <Input
                          type="text"
                          value={formNama}
                          onChange={(e) => setFormNama(e.target.value)}
                          placeholder="Contoh: Vanilla Milk"
                          className="h-8 text-xs font-bold text-[#302621] bg-white"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                          Kode
                        </Label>
                        <Input
                          type="text"
                          value={formKode}
                          onChange={(e) => setFormKode(e.target.value)}
                          placeholder="LZ-012"
                          className="h-8 text-xs font-mono font-medium bg-white"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <Label className="text-[10px] font-bold text-slate-600 mb-1 block" title="Jumlah botol sample">
                          Jumlah (Qty)
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          max={100}
                          value={formQty}
                          onChange={(e) => setFormQty(Math.max(1, parseInt(e.target.value) || 1))}
                          className="h-8 text-xs font-bold text-center bg-white"
                        />
                      </div>
                    </div>

                    {/* Tombol Tambahkan */}
                    <div className="flex justify-end pt-1">
                      <Button
                        type="button"
                        onClick={handleAddToQueue}
                        disabled={!formNama.trim()}
                        className="h-8 text-xs bg-[#8C6208] hover:bg-[#6f4d06] text-white font-bold gap-1.5 px-4 rounded-lg cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Tambah ke Daftar Cetak
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Tabel Antrean Cetak (Full Width) */}
              <div className="bg-white p-4 rounded-xl border border-[#E6D5B8] shadow-2xs flex-1 flex flex-col min-h-[260px]">
                <div className="flex items-center justify-between border-b border-[#F0E8D9] pb-2.5 mb-2.5">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleToggleSelectAll}
                      className="text-slate-600 hover:text-slate-900 transition-colors p-0.5 cursor-pointer"
                      title={isAllSelected ? 'Batal pilih semua' : 'Pilih semua'}
                    >
                      {isAllSelected ? (
                        <CheckSquare className="w-4 h-4 text-[#8C6208]" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                    <h5 className="text-xs font-black text-[#302621] uppercase tracking-wider">
                      Daftar Stiker yang Akan Dicetak ({queueItems.length} Item)
                    </h5>
                    <span className="text-[10px] text-slate-400 font-normal hidden sm:inline">
                      — Edit nama vendor, item, kode, & qty langsung di baris
                    </span>
                  </div>

                  {queueItems.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setQueueItems([])}
                      className="text-[10px] font-bold text-rose-600 hover:text-rose-800 transition-colors cursor-pointer px-2 py-1 rounded hover:bg-rose-50"
                    >
                      Kosongkan Daftar
                    </button>
                  )}
                </div>

                {/* Table Header Bar for Clear Structure */}
                {queueItems.length > 0 && (
                  <div className="hidden sm:flex items-center gap-2.5 px-3 py-1.5 bg-slate-100/70 rounded-lg text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2 border border-slate-200/60">
                    <span className="w-5 shrink-0 text-center">✓</span>
                    <span className="w-36 md:w-52 shrink-0">Nama Vendor</span>
                    <span className="flex-1 min-w-[200px]">Nama Item / Bibit</span>
                    <span className="w-24 md:w-36 shrink-0 text-center">Kode</span>
                    <span className="w-24 shrink-0 text-center">Jumlah (Qty)</span>
                    <span className="w-16 shrink-0 text-right">Aksi</span>
                  </div>
                )}

                {/* List items */}
                <div className="flex-1 overflow-y-auto space-y-2 max-h-[380px] pr-1">
                  {queueItems.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Layers className="w-8 h-8 mx-auto mb-2 opacity-40 text-[#8C6208]" />
                      <p className="text-xs font-semibold">Belum ada item di daftar cetak</p>
                      <p className="text-[11px] mt-0.5">Pilih kategori di atas lalu klik &quot;Tambah ke Daftar Cetak&quot;</p>
                    </div>
                  ) : (
                    queueItems.map((item) => (
                      <div
                        key={item.id}
                        className={`p-2.5 rounded-lg border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 ${
                          item.selected
                            ? 'bg-[#FAF7F2] border-[#E6D5B8]'
                            : 'bg-slate-50/60 border-slate-200 opacity-60'
                        }`}
                      >
                        {/* Checkbox & Inline Edit Inputs */}
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          <button
                            type="button"
                            onClick={() => handleToggleSelect(item.id)}
                            className="shrink-0 p-0.5 cursor-pointer"
                          >
                            {item.selected ? (
                              <CheckSquare className="w-4 h-4 text-[#8C6208]" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>

                          {/* Editable Vendor */}
                          <div className="w-36 md:w-52 shrink-0">
                            <Input
                              type="text"
                              value={item.vendor}
                              onChange={(e) => handleUpdateQueueItem(item.id, 'vendor', e.target.value)}
                              placeholder="Vendor..."
                              className="h-8 text-xs bg-white border-slate-200 px-2.5 font-medium"
                              title="Edit Nama Vendor"
                            />
                          </div>

                          {/* Editable Nama Item */}
                          <div className="flex-1 min-w-[200px]">
                            <Input
                              type="text"
                              value={item.nama_item}
                              onChange={(e) => handleUpdateQueueItem(item.id, 'nama_item', e.target.value)}
                              placeholder="Nama Bibit / Item..."
                              className="h-8 text-xs font-bold text-[#302621] bg-white border-slate-200 px-2.5"
                              title="Edit Nama Item / Bibit"
                            />
                          </div>

                          {/* Editable Kode */}
                          <div className="w-24 md:w-36 shrink-0">
                            <Input
                              type="text"
                              value={item.kode}
                              onChange={(e) => handleUpdateQueueItem(item.id, 'kode', e.target.value)}
                              placeholder="Kode"
                              className="h-8 text-xs font-mono bg-white border-slate-200 px-2 text-center"
                              title="Edit Kode"
                            />
                          </div>
                        </div>

                        {/* Qty Stepper & Actions */}
                        <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
                          <div className="flex items-center gap-1 bg-white rounded-lg p-0.5 border border-slate-200 w-24 justify-between">
                            <button
                              type="button"
                              onClick={() =>
                                handleUpdateQueueItem(item.id, 'qty', Math.max(1, Number(item.qty) - 1))
                              }
                              className="w-6 h-6 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100 rounded cursor-pointer"
                              title="Kurangi jumlah stiker"
                            >
                              -
                            </button>
                            <span className="text-xs font-bold text-[#8C6208]">
                              {item.qty}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleUpdateQueueItem(item.id, 'qty', Number(item.qty) + 1)
                              }
                              className="w-6 h-6 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100 rounded cursor-pointer"
                              title="Tambah jumlah stiker"
                            >
                              +
                            </button>
                          </div>

                          <div className="flex items-center gap-1 w-16 justify-end">
                            <button
                              type="button"
                              onClick={() => handleDuplicateQueueItem(item.id)}
                              className="p-1.5 text-slate-400 hover:text-[#8C6208] hover:bg-[#FAF7F2] rounded transition-colors cursor-pointer"
                              title="Duplikat baris ini"
                            >
                              <Copy className="w-4 h-4" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleRemoveQueueItem(item.id)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                              title="Hapus dari daftar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#E6D5B8] bg-white shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              Tutup
            </Button>

            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-slate-600">
                Total: <strong className="text-[#302621] font-bold">{stickersToPrint.length} Stiker Sample</strong>
              </span>

              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPreviewModal(true)}
                disabled={stickersToPrint.length === 0}
                className="border-[#D4AF37] bg-[#FAF7F2] hover:bg-[#F3ECE1] text-[#8C6208] text-xs font-bold px-4 h-9 rounded-xl shadow-xs gap-2 cursor-pointer transition-all disabled:opacity-50"
              >
                <Eye className="w-4 h-4 text-[#8C6208]" />
                <span>Preview Stiker ({stickersToPrint.length})</span>
              </Button>

              <Button
                type="button"
                onClick={handlePrint}
                disabled={stickersToPrint.length === 0}
                className="bg-[#302621] hover:bg-[#1A1411] text-[#D4AF37] border border-[#D4AF37]/40 text-xs font-bold px-5 h-9 rounded-xl shadow-md gap-2 cursor-pointer transition-all disabled:opacity-50"
              >
                <Printer className="w-4 h-4 text-[#D4AF37]" />
                <span>Cetak {stickersToPrint.length} Stiker</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Preview Stiker Sample */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-6xl w-[96vw] max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden bg-white rounded-2xl border border-[#E6D5B8] shadow-2xl">
          <div className="flex items-center justify-between pl-5 pr-14 sm:pr-16 py-4 border-b border-[#E6D5B8] bg-[#FAF7F2] shrink-0 flex-wrap gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#8C6208]/15 text-[#8C6208] flex items-center justify-center font-bold">
                <Eye className="w-4 h-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-sm md:text-base font-bold text-[#302621]">
                    Preview Label Sample (40 × 20 mm) — {stickersToPrint.length} Stiker
                  </DialogTitle>
                  <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#8C6208]/10 text-[#8C6208] border border-[#8C6208]/20">
                    4 Stiker Kesamping (Format A4)
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Format stiker rapi siap cetak di kertas A4 (penomoran otomatis jika jumlah &gt; 1)
                </p>
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-white px-2.5 py-1 rounded-xl border border-[#E6D5B8] shadow-2xs">
              <button
                type="button"
                onClick={() => setPreviewZoom((z) => Math.max(0.8, Number((z - 0.1).toFixed(1))))}
                className="p-1 hover:text-slate-900 text-slate-500 cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs font-mono font-bold text-[#8C6208] px-1">
                {Math.round(previewZoom * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setPreviewZoom((z) => Math.min(2.0, Number((z + 0.1).toFixed(1))))}
                className="p-1 hover:text-slate-900 text-slate-500 cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Preview Canvas - Responsif Mengikuti Ukuran Layar Masing-Masing */}
          <div className="flex-1 overflow-y-auto p-6 bg-[#F5F2EC] flex flex-wrap gap-4 items-center justify-center min-h-[320px]">
            {stickersToPrint.map((item, idx) => (
              <div key={`prev-${item.id}-${idx}`} className="flex flex-col items-center gap-1.5 animate-in fade-in duration-100">
                <span className="text-[10px] font-bold text-slate-500 bg-white/80 px-2 py-0.5 rounded-full border border-slate-200 shadow-2xs self-start">
                  #{idx + 1} • {item.itemName}
                </span>
                <div
                  style={{
                    width: `${40 * previewZoom}mm`,
                    height: `${20 * previewZoom}mm`,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  }}
                  className="bg-white rounded-none shrink-0 border border-slate-200"
                >
                  <StokSampleLabelSticker
                    vendorName={item.vendor}
                    itemName={item.itemName}
                    code={item.code}
                    tanggal={item.tanggal}
                    labelTag={item.labelTag}
                    scale={previewZoom}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Preview Modal Footer */}
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#E6D5B8] bg-white shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPreviewModal(false)}
              className="text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              Kembali ke Edit
            </Button>

            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">
                Total siap cetak: <strong className="text-[#302621]">{stickersToPrint.length} Stiker</strong> (5 kolom pada kertas A4)
              </span>
              <Button
                type="button"
                onClick={() => {
                  setShowPreviewModal(false);
                  handlePrint();
                }}
                className="bg-[#302621] hover:bg-[#1A1411] text-[#D4AF37] border border-[#D4AF37]/40 text-xs font-bold px-5 h-9 rounded-xl shadow-md gap-2 cursor-pointer transition-all"
              >
                <Printer className="w-4 h-4 text-[#D4AF37]" />
                <span>Cetak Sekarang</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* A4 Print Sheet Container Portaled Directly to document.body (5 Kolom Kesamping) */}
      {isOpen &&
        stickersToPrint.length > 0 &&
        createPortal(
          <div id="stok-sample-print-container">
            <div
              style={{
                width: '210mm',
                minHeight: '297mm',
                padding: '10mm 1mm',
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 40mm)',
                gridAutoRows: '20mm',
                justifyContent: 'center',
                alignContent: 'start',
                columnGap: '2mm',
                rowGap: '2mm',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
              }}
            >
              {stickersToPrint.map((item, index) => (
                <StokSampleLabelSticker
                  key={`sample-stk-${item.id || index}`}
                  vendorName={item.vendor}
                  itemName={item.itemName}
                  code={item.code}
                  tanggal={item.tanggal}
                  labelTag={item.labelTag}
                />
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
